import React, { createContext, useContext } from 'react';
import {instance} from './config/matomo';
import {MatomoProvider} from '@jonkoops/matomo-tracker-react';

// Default app configuration.
// The widget will overwrite these with the window.DMG_WIDGET_CONFIG variable.
// The id config is just here for documentation purpose. It gets used before the React application loads.
const DEFAULT_CONFIGURATION = {
    id: 'dmg-widget', // The id of the div where the widget should be rendered
    mode: 'events', // If "locations", the location list will be rendered as default page
    branding: null, // Title from the scss file to be included
    stylesheetUrl: null, // Define an additional css file here. Requires the domain to be on the allow-list.
    styles: {
        fontSizeDesktop: null,
        fontSizeMobile: null,
        colorPrimary: null,
        colorSecondary: null,
        fontColor: null,
        fontColorPrimaryButton: null,
        fontColorSecondaryButton: null,
        infoHeadlineColor: null,
        cardHeadlineColor: null,
        bgColor: null,
    },
    noCookieBanner: false,
    defaultListingsView: 'grid', // "list", "grid" or "map"
    smallHeaderImage: false,
    listingTitle: {
        // Define the listing header title for the event and location listing page
        //
        // locations: 'myTitle',
        // events: 'myTitle'
    },
    fixedFilters: {
        // Define some filter values here that you always want to apply, no matter what the user types in.
        // Fixed filters are applied as AND in addition to the filters defined by the user.
        //
        // tags: ['My special tag'], // Only show events and locations with this tag
    },
    filterConfiguration: {
        // Allows configuring the filter elements displayed to the user. This does not affect the fixed filters.
        //
        // fulltext: false, // Disables the fulltext filter completely (works also with all the other filters below)
        // radius:  false,
        // date: false,
        // price: false,
        // categories: [
        //     // Set which categories are available. Every category not listed here, will be added as "Sonstige".
        //     'Bühne & Musik',
        //     'Feste, Märkte & Spaß',
        //     'Sport & Aktiv',
        // ],
        // tags: [
        //     // Set which tags can be quick-selected through checkboxes
        //     'Geeignet für Familien und Kinder',
        //     'Barrierefrei',
        // ],
        // tagDropdown: {
        //     enabled: true, // Tag dropdown has to explicitly enabled as it is not used by the dmg calendar
        //     label: 'Special tags', // Show an optional label above the dropdown
        //     description: 'This is my helptext', // An optional description for the dropdown
        //     placeholder: 'Choose a tag',
        //     tags: [
        //         'A Tag',
        //         'Another Tag',
        //     ],
        // },
        // dateOption: {
        //     default: 'arbitrary'
        // },
    },
    eventsFilterConfiguration: {
        // Filter configuration specific for the events listing, gets merged with the global filter configuration
        // above.
    },
    locationsFilterConfiguration: {
        // Filter configuration specific for the locations listing, gets merged with the global filter configuration
        // above.
    },
    mapConfiguration: {
        // Enable "Include events" and "Include locations" buttons in the map
        allowLoadingOtherResources: false,
        // Enable custom location markers introduced for the Erlebnisregion
        enableCustomLocationMarkers: false,
    },
    // Callback to e.g. update the breadcrumbs on your site
    onRouteChange: (routeName, pageTitle) => null,
};

const AppConfigurationContext = createContext(DEFAULT_CONFIGURATION);
AppConfigurationContext.displayName = 'AppConfiguration';

export const useAppConfiguration = () => useContext(AppConfigurationContext);

function deepMergeConfig(baseConfig, configToMerge, dontDeepJoinKeys= []) {
    for (const key in baseConfig) {
        if (!Object.prototype.hasOwnProperty.call(baseConfig, key)) {
            continue;
        }

        if (typeof configToMerge[key] === 'undefined') {
            // Nothing to merge
            continue;
        }

        if (
            typeof baseConfig[key] === 'object'
            && baseConfig[key] !== null
            && !Array.isArray(baseConfig[key])
            && !dontDeepJoinKeys.includes(key)
        ) {
            deepMergeConfig(baseConfig[key], configToMerge[key]);

            continue;
        }

        baseConfig[key] = configToMerge[key];
    }
}

const AppConfiguration = ({config = {}, isWidget, children}) => {
    // Merge with default configuration
    const resolvedAppConfiguration = {...DEFAULT_CONFIGURATION};
    deepMergeConfig(resolvedAppConfiguration, config, [
        'listingTitle',
        'fixedFilters',
        'filterConfiguration',
        'eventsFilterConfiguration',
        'locationsFilterConfiguration',
    ]);

    // Some configuration are for all widgets which is what we use this boolean for
    resolvedAppConfiguration.isWidget = isWidget;

    // Merge the global filter configuration into the events and locations filter configuration
    resolvedAppConfiguration.eventsFilterConfiguration = {
        ...resolvedAppConfiguration.filterConfiguration,
        ...resolvedAppConfiguration.eventsFilterConfiguration,
    };
    resolvedAppConfiguration.locationsFilterConfiguration = {
        ...resolvedAppConfiguration.filterConfiguration,
        ...resolvedAppConfiguration.locationsFilterConfiguration,
    };

    if (isWidget) {
        // We don't use matomo in the widget
        return (
            <AppConfigurationContext.Provider value={resolvedAppConfiguration}>
                {children}
            </AppConfigurationContext.Provider>
        );
    }

    return (
        <AppConfigurationContext.Provider value={resolvedAppConfiguration}>
            <MatomoProvider value={instance}>
                {children}
            </MatomoProvider>
        </AppConfigurationContext.Provider>
    );
};

export default AppConfiguration;
